"use client";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	type AnchorHTMLAttributes,
	type ButtonHTMLAttributes,
	createElement,
	forwardRef,
} from "react";
import { cn } from "../../../..";

type ComponentTypes = "a" | "button";

type ComponentVariant = AnchorHTMLAttributes<HTMLAnchorElement> &
	ButtonHTMLAttributes<HTMLButtonElement>;

export interface SquareButtonProps {
	component: ComponentTypes;
	title: string;
	icon?: IconProp;
	color?: "shades-light" | "shades";
	size?: "small" | "medium" | "large";
}

export const SquareButton = forwardRef<ComponentVariant, SquareButtonProps & ComponentVariant>(
	function Ref(props, ref) {
		const {
			component,
			title,
			icon,
			color = "shades-light",
			size = "medium",
			className,
			children,
			...remainingProps
		} = props;

		return createElement(
			component,
			{
				...remainingProps,
				title: title,
				type: component === "button" ? (remainingProps.type ?? "button") : undefined,
				className: cn(
					"flex shrink-0 appearance-none items-center justify-center rounded p-1 focus:outline-none motion-safe:transition sm:p-2",
					{
						"h-8 w-8 text-xl": size === "small",
						"h-10 w-10 text-2xl": size === "medium",
						"h-12 w-12 text-4xl": size === "large",
						"hover:bg-shades focus-visible:bg-shades": color === "shades",
						"hover:bg-shades-light focus-visible:bg-shades-light": color === "shades-light",
					},
					className,
				),
				ref,
			},
			<>
				{children}
				{icon && <FontAwesomeIcon icon={icon} fixedWidth />}
			</>,
		);
	},
);
